import { Button, Result, Skeleton } from "antd"
import React, {
  type ComponentProps,
  type ComponentType,
  type ReactNode,
  Suspense,
  lazy,
  useSyncExternalStore
} from "react"
import { useTranslation } from "react-i18next"

import { whileRecoveringImport } from "../../../utils/handlers/chunk-error-handlers.ts"
import {
  isChunkLoadError,
  isStylesheetPreloadError
} from "../../../utils/handlers/is-chunk-load-error.ts"
import { reloadForChunkError } from "../../../utils/handlers/reload-for-chunk-error.ts"

// Waits before each retry of a screen's file that failed to arrive; after the
// last one fails too, the app reloads.
export const LAZY_PAGE_RETRY_DELAYS_MS = [500, 1500] as const

interface LoadStatus {
  offline: boolean
  retryNow?: () => void
}

const createLoadStatus = () => {
  let status: LoadStatus = { offline: false }
  const listeners = new Set<() => void>()
  return {
    read: () => status,
    write: (next: LoadStatus) => {
      status = next
      listeners.forEach((listener) => listener())
    },
    subscribe: (listener: () => void) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    }
  }
}

type LoadStatusStore = ReturnType<typeof createLoadStatus>

const isOffline = () =>
  typeof navigator !== "undefined" && navigator.onLine === false

const pause = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms))

// Resolves when the browser is back online or the user presses Retry.
const untilBackOnline = (status: LoadStatusStore) =>
  new Promise<void>((resolve) => {
    const retry = () => {
      window.removeEventListener("online", retry)
      status.write({ offline: false })
      resolve()
    }
    window.addEventListener("online", retry)
    status.write({ offline: true, retryNow: retry })
  })

// A screen's file is fetched the first time it is opened, so a dropped
// request on weak Wi-Fi must not reload the whole app. The file is asked for
// again twice, then a reload is the last resort, which is also what a tab
// still running a replaced release needs. Offline, nothing is reloaded: the
// screen says so and tries again when the connection returns.
//
// The retry calls the same `import()` again rather than a cache-busted URL: the
// loaders map named exports, and a failed static dependency of the screen's
// file stays cached under its own URL anyway. Browsers that re-fetch a failed
// module recover here; one that remembers the failure fails the retries at
// once, and the reload below takes over, as before.
const loadWithRecovery = async <T,>(
  load: () => Promise<T>,
  status: LoadStatusStore
): Promise<T> => {
  let onlineFailures = 0
  for (;;) {
    try {
      return await whileRecoveringImport(load)
    } catch (error: unknown) {
      if (!isChunkLoadError(error)) throw error

      // A failed stylesheet is never asked for again by a retried import, so
      // retrying would render the screen unstyled: reload instead, once online.
      const stylesheetFailed = isStylesheetPreloadError(error)

      if (isOffline()) {
        await untilBackOnline(status)
        if (!stylesheetFailed) continue
      }

      if (
        stylesheetFailed ||
        onlineFailures >= LAZY_PAGE_RETRY_DELAYS_MS.length
      ) {
        reloadForChunkError(error)
        return new Promise<T>(() => {})
      }
      await pause(LAZY_PAGE_RETRY_DELAYS_MS[onlineFailures])
      onlineFailures += 1
    }
  }
}

function LazyPageFallback({
  status,
  loading
}: {
  status: LoadStatusStore
  loading: ReactNode
}) {
  const { t } = useTranslation()
  const { offline, retryNow } = useSyncExternalStore(
    status.subscribe,
    status.read,
    status.read
  )

  if (!offline) return loading

  return (
    <Result
      status="warning"
      title={t("lazyPage.offline")}
      extra={
        <Button type="primary" onClick={retryNow}>
          {t("common.retry")}
        </Button>
      }
    />
  )
}

export function lazyPage<T extends ComponentType<any>>(
  load: () => Promise<{ default: T }>,
  {
    loading = <Skeleton active style={{ padding: 24 }} />
  }: {
    loading?: ReactNode
  } = {}
): ComponentType<ComponentProps<T>> {
  const status = createLoadStatus()
  const Page = lazy(() => loadWithRecovery(load, status))

  function LazyPage(props: ComponentProps<T>) {
    return (
      <Suspense
        fallback={<LazyPageFallback status={status} loading={loading} />}
      >
        <Page {...props} />
      </Suspense>
    )
  }

  return LazyPage
}
